<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesPartyProgressBar from './SalesPartyProgressBar.vue';

const props = defineProps({
  progress: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['select']);
const { t } = useI18n();

const value = key =>
  Math.min(100, Math.max(0, Number(props.progress?.[key] || 0)));
const receiptReady = computed(() => {
  if (typeof props.progress?.receipt_ready === 'boolean') {
    return props.progress.receipt_ready;
  }

  if (props.progress?.validar_imovel && props.progress?.validar_vendedor) {
    return true;
  }

  return value('imovel') === 100 && value('vendedores') === 100;
});
const cards = computed(() => [
  {
    key: 'imovel',
    label: t('SALES.PROGRESS.PROPERTY'),
    icon: value('imovel') === 100 ? 'i-lucide-check' : 'i-lucide-house',
  },
  {
    key: 'vendedores',
    label: t('SALES.PROGRESS.SELLERS'),
    icon: value('vendedores') === 100 ? 'i-lucide-check' : 'i-lucide-users',
  },
  {
    key: 'compradores',
    label: t('SALES.PROGRESS.BUYERS'),
    icon: value('compradores') === 100 ? 'i-lucide-check' : 'i-lucide-users',
  },
  {
    key: 'comissao',
    label: t('SALES.PROGRESS.COMMISSION'),
    icon: value('comissao') === 100 ? 'i-lucide-check' : 'i-lucide-house',
  },
  {
    key: 'recibo',
    label: t('SALES.PROGRESS.RECEIPT'),
    subtitle: t('SALES.REGISTRATION.RECEIPT_SUBTITLE'),
    icon: value('recibo') === 100 ? 'i-lucide-check' : 'i-lucide-file-text',
    disabled: !receiptReady.value,
  },
]);

const status = key => {
  const percent = value(key);
  if (percent === 100) return t('SALES.STATUS.COMPLETED');
  if (percent > 1) return t('SALES.STATUS.DOING');
  return t('SALES.STATUS.PENDING');
};
const statusClass = key => {
  const percent = value(key);
  if (percent === 100) return 'bg-emerald-100 text-emerald-700';
  if (percent > 1) return 'bg-lais-primary-50 text-lais-primary';
  return 'bg-amber-100 text-amber-700';
};
const selectCard = card => {
  if (!card.disabled) emit('select', card.key);
};
</script>

<template>
  <section
    class="grid gap-4 md:grid-cols-2 xl:grid-cols-5"
    :aria-label="t('SALES.ARIA.PROGRESS')"
  >
    <LaisCard
      v-for="card in cards"
      :key="card.key"
      :title="card.label"
      :prefix-icon="card.icon"
      :interactive="!card.disabled"
      class="min-w-0"
      :class="[card.disabled ? 'cursor-not-allowed opacity-60' : '']"
      :aria-disabled="card.disabled"
      @click="selectCard(card)"
    >
      <div class="grid gap-3">
        <div class="flex items-center justify-between gap-2">
          <span
            class="rounded-full px-2 py-1 text-xs font-bold uppercase tracking-wide"
            :class="statusClass(card.key)"
            :data-test="`sales-registration-status-${card.key}`"
          >
            {{ status(card.key) }}
          </span>
          <span class="text-sm font-bold text-lais-primary">
            {{ t('SALES.COMMON.PERCENT', { value: value(card.key) }) }}
          </span>
        </div>
        <p v-if="card.subtitle" class="text-xs text-lais-text-muted">
          {{ card.subtitle }}
        </p>
        <SalesPartyProgressBar :label="card.label" :value="value(card.key)" />
      </div>
    </LaisCard>
  </section>
</template>
